import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { get } from 'aws-amplify/api';
import { Link } from 'react-router-dom';
import { usePreferences } from '../contexts/PreferencesContext';
import CalorieWidget from './CalorieWidget';

interface MealSummary {
  totalCalories: number;
  mealCount: number;
  lastMealDate: string | null;
}

interface Recommendation {
  recommendations: string;
}

const Home: React.FC = () => {
  const { preferences } = usePreferences();
  const [mealSummary, setMealSummary] = useState<MealSummary>({
    totalCalories: 0,
    mealCount: 0,
    lastMealDate: null
  });
  const [recommendation, setRecommendation] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [recommendationError, setRecommendationError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserAndData = async () => {
      try {
        // Get today's date
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0]; // Format: YYYY-MM-DD

        const session = (await fetchAuthSession()) ?? {};
        const authToken = session.tokens?.idToken?.toString();

        if (!authToken) {
          throw new Error('Not authenticated');
        }

        // Get meals for today
        const restOperation = get({
          apiName: 'FoodTrackerAPI',
          path: '/food/meals',
          options: {
            queryParams: {
              date: dateStr
            },
            headers: {
              Authorization: authToken
            }         
          }
        });

        const { body } = await restOperation.response;
        const response = await body.json() as { meals: any[] };
        
        // Calculate summary
        if (response && response.meals) {
          const meals = response.meals;
          const totalCalories = meals.reduce((sum: number, meal: any) => sum + (meal.totalCalories || 0), 0);
          
          setMealSummary({
            totalCalories,
            mealCount: meals.length,
            lastMealDate: meals.length > 0 ? new Date(meals[0].timestamp * 1000).toLocaleTimeString() : null
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Failed to load your meal data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserAndData();
  }, []);

  // Temporarily disabled - Meal Recommendations
  /*
  useEffect(() => {
    const fetchRecommendations = async () => {
      try {
        setRecommendationLoading(true);
        
        const session = await fetchAuthSession();
        const authToken = session.tokens?.idToken?.toString();

        if (!authToken) {
          throw new Error('Not authenticated');
        }

        // Get meal recommendations
        const restOperation = get({
          apiName: 'FoodTrackerAPI',
          path: '/food/recommendations',
          options: {
            headers: {
              Authorization: authToken
            }         
          }
        });

        const { body } = await restOperation.response;
        const response = await body.json() as {
          recommendations: string;
        };
        
        if (response && response.recommendations) {
          setRecommendation(response.recommendations);
        } else {
          throw new Error('Invalid recommendation data');
        }
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setRecommendationError('Failed to load meal recommendations. Please try again later.');
      } finally {
        setRecommendationLoading(false);
      }
    };
    
    fetchRecommendations();
  }, []);
  */

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div>
      <CalorieWidget
        totalCalories={mealSummary.totalCalories}
        mealCount={mealSummary.mealCount}
        loading={loading}
        error={error}
      />

      {/* Temporarily disabled - Meal Recommendations
      <div className="card">
        <h3 className="card-title">Meal Recommendations</h3>
        {recommendationLoading ? (
          <div className="loading-inline">
            <div className="loading-spinner-small"></div>
            <span>Loading recommendations...</span>
          </div>
        ) : recommendationError ? (
          <div className="error-message">{recommendationError}</div>
        ) : (
          <div className="recommendation-content">
            <p>{recommendation}</p>
          </div>
        )}
      </div>
      */}
    </div>
  );
};

export default Home;
